import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';
import MedicationOverviewTab from '../components/medicationHub/MedicationOverviewTab';
import MedicationNotificationsTab from '../components/medicationHub/MedicationNotificationsTab';
import AllocatedMedicationTab from '../components/medicationHub/AllocatedMedicationTab';
import PendingCDApprovalsTab from '../components/medicationHub/PendingCDApprovalsTab';
import ControlledDrugsBookTab from '../components/medicationHub/ControlledDrugsBookTab';
import AssignMedicationModal from '../components/medicationHub/AssignMedicationModal';
import MedicationStatisticsTab from '../components/medicationHub/MedicationStatisticsTab';
import MedicationReportingTab from '../components/medicationHub/MedicationReportingTab';

// Placeholder components for the other tabs
const PlaceholderTab = ({ name }) => (
    <div style={{ padding: '40px', textAlign: 'center', backgroundColor: 'white', borderRadius: '10px', color: '#718096' }}>
        <h2>{name}</h2>
        <p>This tab is currently under construction (Phase 2).</p>
    </div>
);

const MedicationHubPage = () => {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const { userRole } = useAuth();
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');

    // Top-level tabs matching Flutter application
    const topLevelTabs = [
        { id: 'overview', label: 'Medication Overview' },
        { id: 'notifications', label: 'Medication Notifications' },
        { id: 'allocated', label: 'Allocated Medication' },
        { id: 'statistics', label: 'Medication Statistics' },
        { id: 'reporting', label: 'Medication Reporting' },
        { id: 'pendingCD', label: 'Pending CD Approvals' },
        { id: 'cdBook', label: 'Controlled Drugs Book' }
    ];

    const [activeTopTab, setActiveTopTab] = useState('overview');

    const renderActiveTab = () => {
        switch (activeTopTab) {
            case 'overview':
                return <MedicationOverviewTab serverId={serverId} />;
            case 'notifications':
                return <MedicationNotificationsTab serverId={serverId} />;
            case 'allocated':
                return <AllocatedMedicationTab serverId={serverId} />;
            case 'statistics':
                return <MedicationStatisticsTab serverId={serverId} />;
            case 'reporting':
                return <MedicationReportingTab serverId={serverId} />;
            case 'pendingCD':
                return <PendingCDApprovalsTab serverId={serverId} />;
            case 'cdBook':
                return <ControlledDrugsBookTab serverId={serverId} />;
            default:
                return <MedicationOverviewTab serverId={serverId} />;
        }
    };

    return (
        <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto', fontFamily: '"Inter", sans-serif' }}>
            
            {/* Header Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', color: '#2d3748', margin: '0 0 5px 0' }}>Medication Hub</h1>
                    <p style={{ color: '#718096', margin: 0, fontSize: '15px' }}>Centralized medication management and compliance tracking.</p>
                </div>
                <div>
                    <button 
                        onClick={() => navigate(`/servers/${serverId}`)}
                        style={{ padding: '10px 20px', backgroundColor: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', marginRight: '15px' }}
                    >
                        Back to Server
                    </button>
                    {canEdit && (
                        <button 
                            onClick={() => setIsAssignModalOpen(true)}
                            style={{ padding: '10px 20px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', boxShadow: '0 2px 4px rgba(49,130,206,0.3)' }}
                        >
                            + Assign New Medication
                        </button>
                    )}
                </div>
            </div>

            {/* Top-Level Tabs Navigation */}
            <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginBottom: '24px', 
                borderBottom: '1px solid #e5e7eb', 
                paddingBottom: '12px',
                overflowX: 'auto',
                whiteSpace: 'nowrap'
            }}>
                {topLevelTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTopTab(tab.id)}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px',
                            transition: 'all 0.2s',
                            backgroundColor: activeTopTab === tab.id ? '#1f2937' : '#f3f4f6',
                            color: activeTopTab === tab.id ? 'white' : '#4b5563',
                            boxShadow: activeTopTab === tab.id ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Render Active Tab Content */}
            <div style={{ minHeight: '500px' }}>
                {renderActiveTab()}
            </div>

            {isAssignModalOpen && (
                <AssignMedicationModal 
                    serverId={serverId} 
                    onClose={() => setIsAssignModalOpen(false)} 
                    onSuccess={() => {
                        setIsAssignModalOpen(false);
                        setActiveTopTab('overview');
                    }}
                />
            )}

        </div>
    );
};

export default MedicationHubPage;
